import { useState, useEffect, forwardRef, useMemo, useCallback } from 'react'
import { Box, Dialog, Slide, CircularProgress, Button } from '@mui/material'
import { Close } from '@mui/icons-material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { useReportsContext, useUser } from '../context'
import reportsRoutes from '../routes/reports.routes'

const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${day}/${month}/${year} ${hours}:${minutes}`
}

const ordenarPorNumeroOt = (a, b) => {
    if (a.index < b.index) {
        return 1
    }
    if (a.index > b.index) {
        return -1
    }
    return 0
}

const Transition = forwardRef(function Transition (props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

const ReportsResumeDialog = ({ open, reports, handleClose }) => {
    const { siteSelected } = useReportsContext()
    const { isOperator } = useUser()

    const [totalReports, setTotalReports] = useState(0)
    const [totalAutomatic, setTotalAutomatic] = useState(0)
    const [totalManual, setTotalManual] = useState(0)

    const [automaticReports, setAutomaticReports] = useState([])
    const [manualReports, setManualReports] = useState([])

    const [skipAutomatic, setSkipAutomatic] = useState(0)
    const [skipManual, setSkipManual] = useState(0)

    const [limitAutomatic, setLimitAutomatic] = useState(30)
    const [limitManual, setLimitManual] = useState(30)

    const [loadingAutomatic, setLoadingAutomatic] = useState(false)
    const [loadingManual, setLoadingManual] = useState(false)

    const [mostrarAlertasAutomaticas, setMostrarAlertasAutomaticas] =
		useState(false)
    const [mostrarAlertasManuales, setMostrarAlertasManuales] = useState(false)

    // Filter and prepare local automatic reports (for operators/offline)
    const localAutomaticAll = useMemo(() => {
        if (!reports) return []
        return reports
            .filter((r) =>
                r.isAutomatic !== undefined ? r.isAutomatic : !!r.description
            )
            .map((r) => ({ ...r, index: r.newIdIndex ? r.newIdIndex : r.idIndex }))
            .sort(ordenarPorNumeroOt)
    }, [reports])

    // Filter and prepare local manual reports (for operators/offline)
    const localManualAll = useMemo(() => {
        if (!reports) return []
        return reports
            .filter(
                (r) => !(r.isAutomatic !== undefined ? r.isAutomatic : !!r.description)
            )
            .map((r) => ({ ...r, index: r.newIdIndex ? r.newIdIndex : r.idIndex }))
            .sort(ordenarPorNumeroOt)
    }, [reports])

    const fetchSummary = useCallback(
        async (signal) => {
            setLoadingAutomatic(true)
            setLoadingManual(true)
            try {
                const siteId =
					siteSelected === 'all' ||
					siteSelected?._id === 'all' ||
					siteSelected?.idObra === 'none'
					    ? 'all'
					    : siteSelected?._id || siteSelected
                const response = await reportsRoutes.getReportsSummary(
                    siteId,
                    0,
                    30,
                    undefined,
                    { signal }
                )
                if (response) {
                    setTotalReports(response.total || 0)
                    setTotalAutomatic(response.totalAutomatic || 0)
                    setTotalManual(response.totalManual || 0)

                    const mapReports = (list) =>
                        (list || [])
                            .map((r) => ({
                                ...r,
                                index: r.newIdIndex ? r.newIdIndex : r.idIndex
                            }))
                            .sort(ordenarPorNumeroOt)

                    setAutomaticReports(mapReports(response.automaticReports))
                    setManualReports(mapReports(response.manualReports))
                }
            } catch (error) {
                if (error.name === 'AbortError') return
                console.error('Error fetching reports summary:', error)
            } finally {
                setLoadingAutomatic(false)
                setLoadingManual(false)
            }
        },
        [siteSelected]
    )

    const fetchMoreAutomatic = useCallback(async () => {
        setLoadingAutomatic(true)
        try {
            const siteId =
				siteSelected === 'all' ||
				siteSelected?._id === 'all' ||
				siteSelected?.idObra === 'none'
				    ? 'all'
				    : siteSelected?._id || siteSelected
            const nextSkip = skipAutomatic + 30
            const response = await reportsRoutes.getReportsSummary(
                siteId,
                nextSkip,
                30,
                'true'
            )
            if (response && response.reports) {
                setSkipAutomatic(nextSkip)
                setAutomaticReports((prev) => {
                    const combined = [...prev, ...response.reports]
                    return combined
                        .map((r) => ({
                            ...r,
                            index: r.newIdIndex ? r.newIdIndex : r.idIndex
                        }))
                        .sort(ordenarPorNumeroOt)
                })
            }
        } catch (error) {
            console.error('Error fetching more automatic reports:', error)
        } finally {
            setLoadingAutomatic(false)
        }
    }, [siteSelected, skipAutomatic])

    const fetchMoreManual = useCallback(async () => {
        setLoadingManual(true)
        try {
            const siteId =
				siteSelected === 'all' ||
				siteSelected?._id === 'all' ||
				siteSelected?.idObra === 'none'
				    ? 'all'
				    : siteSelected?._id || siteSelected
            const nextSkip = skipManual + 30
            const response = await reportsRoutes.getReportsSummary(
                siteId,
                nextSkip,
                30,
                'false'
            )
            if (response && response.reports) {
                setSkipManual(nextSkip)
                setManualReports((prev) => {
                    const combined = [...prev, ...response.reports]
                    return combined
                        .map((r) => ({
                            ...r,
                            index: r.newIdIndex ? r.newIdIndex : r.idIndex
                        }))
                        .sort(ordenarPorNumeroOt)
                })
            }
        } catch (error) {
            console.error('Error fetching more manual reports:', error)
        } finally {
            setLoadingManual(false)
        }
    }, [siteSelected, skipManual])

    useEffect(() => {
        const abortController = new AbortController()
        if (open) {
            if (!isOperator) {
                setTotalReports(0)
                setTotalAutomatic(0)
                setTotalManual(0)
                setAutomaticReports([])
                setManualReports([])
                setSkipAutomatic(0)
                setSkipManual(0)
                fetchSummary(abortController.signal)
            } else {
                setLimitAutomatic(30)
                setLimitManual(30)
            }
        }
        return () => {
            abortController.abort()
        }
    }, [open, siteSelected, isOperator, fetchSummary])

    const handleLoadMoreAutomatic = () => {
        if (isOperator) {
            setLimitAutomatic((prev) => prev + 30)
        } else {
            fetchMoreAutomatic()
        }
    }

    const handleLoadMoreManual = () => {
        if (isOperator) {
            setLimitManual((prev) => prev + 30)
        } else {
            fetchMoreManual()
        }
    }

    const finalTotal = totalReports
    const finalTotalAutomatic = isOperator
        ? localAutomaticAll.length
        : totalAutomatic
    const finalTotalManual = isOperator ? localManualAll.length : totalManual

    const displayedAutomaticReports = isOperator
        ? localAutomaticAll.slice(0, limitAutomatic)
        : automaticReports

    const displayedManualReports = isOperator
        ? localManualAll.slice(0, limitManual)
        : manualReports

    const hasMoreAutomatic = isOperator
        ? limitAutomatic < localAutomaticAll.length
        : automaticReports.length < totalAutomatic

    const hasMoreManual = isOperator
        ? limitManual < localManualAll.length
        : manualReports.length < totalManual

    return (
        <Dialog open={open} TransitionComponent={Transition} onClose={handleClose}>
            <Box
                sx={{
                    height: 700,
                    width: 450,
                    maxWidth: '100%',
                    position: 'relative'
                }}
            >
                <Close
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        cursor: 'pointer'
                    }}
                />
                <div style={{ padding: 20 }}>
                    <h2 style={{ marginTop: 15 }}>Resumen de Ordenes de Trabajo</h2>
                    {(loadingAutomatic || loadingManual) &&
					automaticReports.length === 0 &&
					manualReports.length === 0 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height={400}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <p>
                                    {' '}
                                    <strong>Total OTs: </strong> {finalTotal}{' '}
                                </p>
                                <p
                                    style={{ marginBottom: 0, cursor: 'pointer', width: '100%' }}
                                    onClick={() => {
                                        setMostrarAlertasAutomaticas(!mostrarAlertasAutomaticas)
                                    }}
                                >
                                    {' '}
                                    <strong>OTs Automáticos: </strong> {finalTotalAutomatic}{' '}
                                    <FontAwesomeIcon
                                        style={{ marginLeft: 10 }}
                                        icon={mostrarAlertasAutomaticas ? faChevronUp : faChevronDown}
                                    />{' '}
                                </p>
                                <p style={{ margin: 0, fontSize: 10 }}>
								OT generadas desde el sistema
                                </p>
                                {mostrarAlertasAutomaticas && (
                                    <div
                                        style={{
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            border: '1px solid #ccc',
                                            borderRadius: 8,
                                            padding: 10
                                        }}
                                    >
                                        {displayedAutomaticReports.map((report, i) => {
                                            return (
                                                <p key={i} style={{ margin: '5px 0', fontSize: 13 }}>
												OT {report.newIdIndex || report.idIndex}
                                                    {report.sapId ? ` - SAP: ${report.sapId}` : ''}
                                                    {report.createdAt || report.dateInit
                                                        ? ` - ${formatDate(report.createdAt || report.dateInit)}`
                                                        : ''}
                                                </p>
                                            )
                                        })}
                                        {hasMoreAutomatic && (
                                            <Box display="flex" justifyContent="center" mt={1}>
                                                <Button
                                                    onClick={handleLoadMoreAutomatic}
                                                    disabled={loadingAutomatic}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: '100%' }}
                                                >
                                                    {loadingAutomatic ? 'Cargando...' : '+ Mostrar más'}
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                )}
                                <p
                                    style={{ marginBottom: 0, cursor: 'pointer' }}
                                    onClick={() => {
                                        setMostrarAlertasManuales(!mostrarAlertasManuales)
                                    }}
                                >
                                    {' '}
                                    <strong>OTs Manuales: </strong> {finalTotalManual}{' '}
                                    <FontAwesomeIcon
                                        style={{ marginLeft: 10 }}
                                        icon={mostrarAlertasManuales ? faChevronUp : faChevronDown}
                                    />{' '}
                                </p>
                                <p style={{ margin: 0, fontSize: 10 }}>
								OT generadas por un usuario
                                </p>
                                {mostrarAlertasManuales && (
                                    <div
                                        style={{
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            border: '1px solid #ccc',
                                            borderRadius: 8,
                                            padding: 10
                                        }}
                                    >
                                        {displayedManualReports.map((report, i) => {
                                            return (
                                                <p key={i} style={{ margin: '5px 0', fontSize: 13 }}>
												OT {report.newIdIndex || report.idIndex}
                                                    {report.sapId ? ` - SAP: ${report.sapId}` : ''}
                                                    {report.createdAt || report.dateInit
                                                        ? ` - ${formatDate(report.createdAt || report.dateInit)}`
                                                        : ''}
                                                </p>
                                            )
                                        })}
                                        {hasMoreManual && (
                                            <Box display="flex" justifyContent="center" mt={1}>
                                                <Button
                                                    onClick={handleLoadMoreManual}
                                                    disabled={loadingManual}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: '100%' }}
                                                >
                                                    {loadingManual ? 'Cargando...' : '+ Mostrar más'}
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                </div>
            </Box>
        </Dialog>
    )
}

export default ReportsResumeDialog
